import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, Trash, Key, Eye, EyeSlash, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import EditUserDialog from "@/components/EditUserDialog";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ROLES = ["Manager", "Management User", "Viewer", "CNC Operator", "Manual Machinist", "Helper", "QC Inspector", "Delivery"];

export default function Users() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const users = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data }).data || [];
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", role: "CNC Operator" });
  const [delId, setDelId] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openReset = (u) => { setResetUser(u); setNewPass(""); setConfirmPass(""); setShowPass(false); };

  const resetPassword = async () => {
    if (newPass.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPass !== confirmPass) { toast.error("Passwords do not match"); return; }
    setSavingPass(true);
    try {
      await api.put(`/users/${resetUser.id}`, { password: newPass });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPass(false); }
  };

  const addUser = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    try {
      await api.post("/auth/register", form);
      toast.success("User added");
      qc.invalidateQueries({ queryKey: ["users"] });
      setForm({ name: "", email: "", mobile: "", password: "", role: "CNC Operator" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success(`${u.name} → ${role}`);
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeUser = async () => {
    try {
      await api.delete(`/users/${delId}`);
      toast.success("User removed");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDelId(null); }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-1">Manage Users</h1>
      <p className="text-sm text-slate-500 mb-6">
        Add users and assign roles. Passwords are stored encrypted and never shown. Every role except Manager and
        Management User automatically becomes a Team Member, so operators only see and log their own allocated work.
      </p>

      <Card className="p-5 border-slate-200 shadow-none mb-6">
        <h3 className="font-semibold text-slate-900 mb-4">Add User</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" /></div>
          <div><Label>Email</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email" /></div>
          <div><Label>Mobile</Label><Input className="mt-1.5" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="user-mobile" /></div>
          <div><Label>Password</Label><Input type="password" className="mt-1.5" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password" /></div>
          <div><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}><SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="flex items-end"><Button onClick={addUser} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="user-add-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Add User</Button></div>
        </div>
      </Card>

      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50"><TableRow>
            <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Mobile</TableHead><TableHead>Role</TableHead><TableHead>Employee ID</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.mobile || "—"}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === user?.id}>
                    <SelectTrigger className="h-8 w-44" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell data-testid={`user-team-${u.email}`}>
                  {u.team_code ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-xs">
                      {u.team_code}
                    </Badge>
                  ) : (
                    <span className="text-xs text-slate-400">not a team member</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditUser(u)} title="Edit user" data-testid={`user-edit-${u.email}`}>
                    <PencilSimple className="h-4 w-4 text-slate-600" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={u.id === user?.id} onClick={() => openReset(u)} title="Reset password" data-testid={`user-reset-${u.email}`}>
                    <Key className="h-4 w-4 text-blue-600" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={u.id === user?.id} onClick={() => setDelId(u.id)} data-testid={`user-del-${u.email}`}>
                    <Trash className="h-4 w-4 text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EditUserDialog
        target={editUser}
        roles={ROLES}
        onClose={() => setEditUser(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["entity", "team"] }); }}
      />

      <Dialog open={!!resetUser} onOpenChange={(v) => !v && setResetUser(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for {resetUser?.name} ({resetUser?.email}). Share it securely with the user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New password</Label>
              <div className="relative mt-1.5">
                <Input type={showPass ? "text" : "password"} value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="At least 6 characters" data-testid="reset-new-password" />
                <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="reset-toggle-visibility">
                  {showPass ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirm password</Label>
              <Input type={showPass ? "text" : "password"} className="mt-1.5" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} data-testid="reset-confirm-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUser(null)} data-testid="reset-cancel">Cancel</Button>
            <Button onClick={resetPassword} disabled={savingPass} className="bg-blue-600 hover:bg-blue-700" data-testid="reset-confirm-btn">{savingPass ? "Saving…" : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this user?</AlertDialogTitle><AlertDialogDescription>They will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="user-del-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={removeUser} className="bg-red-600 hover:bg-red-700" data-testid="user-del-confirm">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
